import { useApolloClient } from "@vue/apollo-composable";
import { useStorage } from "@vueuse/core";
import gql from "graphql-tag";
import { isString } from "lodash";
import type { ICurrency } from "~/layers/admin/models";
import type { PendingUserWorkspaceInvite } from "~/layers/admin/modules/auth/runtime/types/api";

export const useWorkspace = () => {
  const { context, fetchAuthContext } = useAuth();
  const current = useStorage<string | undefined>("ws", undefined);

  const workspace = computed(() => context.value?.workspace);

  const currency = computed((): ICurrency | undefined => {
    const code = context.value?.workspace?.currency;
    if (code) {
      return {
        code,
        symbol: currencySymbol(code)!,
      };
    }
  });

  const workspaces = computed(() => context.value?.workspaces || []);
  const pendingInvites = computed(() => context.value?.pendingInvites || []);

  const switchTo = async (code: string) => {
    const { loading } = useLoading();
    const apollo = useApolloClient();
    const apolloClient = apollo.resolveClient();

    loading(true);

    setWorkspace(code); // hold state of this switch

    await wait(2); // wait for storage to be updated

    await fetchAuthContext(); // fetch new workspace and update store
    apolloClient.clearStore(); // reset apollo cache without refetching active queries

    window.location.href = "/"; // reload window
  };

  const isWorkspace = (code: string | undefined) => {
    const wsCodes = context.value?.workspaces?.map((ws) => ws.code) || [];
    return code && wsCodes.includes(code);
  };

  const setWorkspace = (code: string | undefined) => {
    if (isWorkspace(code)) {
      current.value = code;
    }
  };

  const getWorkspace = (): string | undefined => {
    if (isWorkspace(current.value)) {
      return current.value;
    }
    // Clear cached workspace if user has no workspaces or it's no longer valid
    if (
      current.value &&
      context.value?.workspaces !== undefined &&
      workspaces.value.length === 0
    ) {
      current.value = undefined;
    }
    // return context.value?.workspace?.code;
  };

  const acceptInvite = (invite: string | PendingUserWorkspaceInvite) => {
    const { loading } = useLoading();
    const apollo = useApolloClient();
    const apolloClient = apollo.resolveClient();

    const id = isString(invite) ? invite : invite.id;

    loading(true);
    apolloClient
      .mutate<
        {
          wsAcceptInvitation: {
            workspace: string;
          };
        },
        { where: { id: string } }
      >({
        mutation: gql`
          mutation ($where: WsInvitationWhereUniqueInput!) {
            wsAcceptInvitation(where: $where) {
              workspace
            }
          }
        `,
        variables: {
          where: { id },
        },
      })
      .then((data) => {
        if (data?.data?.wsAcceptInvitation.workspace) {
          switchTo(data.data.wsAcceptInvitation.workspace);
        }
      })
      .finally(() => {
        loading(false);
      });
  };

  return {
    currency,
    workspace,
    workspaces,
    pendingInvites,
    switchTo,
    setWorkspace,
    getWorkspace,
    acceptInvite,
  };
};
